import { useCallback } from 'react';
import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { forgotPassword } from 'js/hooks/auth/pas';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useShowNotification } from 'js/components/common/Notifications';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as Types from '../types';

export const useForgotPassword = (
    { userData, setUserData }: Types.IDynamicForgotPasswordRequestParams
) => {
    const translate = useComponentTranslations();
    const getErrorMessage = useGetErrorMessage();
    const isUsernameInUppercase = useAppSelector(prop('isUsernameInUppercase'));
    const { notificationChannels } = useComponentConfig<Types.IDynamicForgotPasswordConfig>();
    const showNotification = useShowNotification({
        iconType: 'info',
        message: translate(notificationChannels ? 'success.withSMS' : 'success')
    });

    return useCallback(async function process({ data, requestReCaptchaToken }: {
        data: Types.IDynamicForgotPasswordFormData,
        requestReCaptchaToken: () => Promise<void>
    }): Promise<string | void> {
        const { pathname, origin } = window.location;
        const { email, userName, birthdate, verificationAnswer } = data;
        const userNameParsed = userName || userData.userName ;

        try {
            const { data: response } = await forgotPassword({
                verificationAnswer,
                email: email || userData.email,
                userName: isUsernameInUppercase ? userNameParsed?.toUpperCase() : userNameParsed,
                birthDate: birthdate || userData.birthdate,
                passwordRecoveryDestinationLink: `${origin}${pathname}`,
                ...notificationChannels && { notificationChannels: 'email,SMS' }
            });

            if (response?.verificationQuestion) {
                setUserData({
                    email, userName, birthdate,
                    verificationQuestion: response.verificationQuestion
                });
            } else {
                showNotification();
                publicEventsAPIService.trigger('forgotPassword:success');
            }
        } catch (error: Types.TStartForgotEmailError) {
            const { data: { errorCode = 0 } = {} } = error?.response || error || {};
            const errorMsg = getErrorMessage(error);

            if (errorCode === 301) {
                try {
                    await requestReCaptchaToken();

                    return await process({
                        data,
                        requestReCaptchaToken
                    });
                } catch {
                    publicEventsAPIService.trigger('forgotPassword:fail');

                    return errorMsg;
                }
            }

            publicEventsAPIService.trigger('forgotPassword:fail');

            return errorMsg;
        }
    }, [userData, setUserData, getErrorMessage, showNotification, notificationChannels, isUsernameInUppercase]);
};
